import React from 'react';

import { Header, rem, Text } from '@vimeo/bokeh';
import { Speedometer, Database, Users, VideosStack } from '@vimeo/bokeh-icons';

import { defaultTemplateProps, PlanData, PlanQuota } from '../../../utils/interface';
import { getPlanQuota } from '../../../utils/utils';
import { BlueHighlight } from '../../common/BlueHighlight';
import { PlanQuotaContainer, StorageQuotaContainer, StyledRow } from './styles';

interface Props {
  planData: PlanData;
  displayMonthlyPlan: boolean;
  modalConfig?: defaultTemplateProps;
  showStorageQuotaModalUpdates?: boolean;
}

const PlanCardQuota = (props: Props) => {
  const { planData, modalConfig, showStorageQuotaModalUpdates } = props;

  const planQuota: PlanQuota | null = getPlanQuota(planData);

  if (!planQuota) return null;

  const { getSeatCount, getVideoCount, getStorageQuota, getBandwidth } = planQuota;
  const { entitlements } = planData.metadata;

  const isStorageQuota: boolean = entitlements?.params?.videoStorageQuotaUnit === 'video_size';
  const seatOverrideCopy = modalConfig?.planOverrides?.[planData.tier]?.seatCopy;

  return (
    <PlanQuotaContainer>
      <StorageQuotaContainer>
        <StyledRow>
          <div>
            <Users
              h={{ base: rem(16), md: rem(20), xl: rem(16) }}
              w={{ base: rem(16), md: rem(20), xl: rem(16) }}
            />
          </div>
          <Text variant={{ base: 'body-md', md: 'body-lg', xl: 'body-md' }}>
            {seatOverrideCopy
              ? `${entitlements?.params?.teamSeats} ${seatOverrideCopy}`
              : getSeatCount(entitlements?.params?.teamSeats, true)}
          </Text>
        </StyledRow>
        <StyledRow>
          {isStorageQuota ? (
            <Database
              h={{ base: rem(16), md: rem(20), xl: rem(16) }}
              w={{ base: rem(16), md: rem(20), xl: rem(16) }}
            />
          ) : (
            <VideosStack
              h={{ base: rem(16), md: rem(20), xl: rem(16) }}
              w={{ base: rem(16), md: rem(20), xl: rem(16) }}
            />
          )}
          {showStorageQuotaModalUpdates ? (
            <BlueHighlight>
              <Header
                size="xl"
                variant={{ base: 'heading-xs', md: 'heading-sm', xl: 'heading-xs' }}
              >
                {isStorageQuota
                  ? getStorageQuota(entitlements?.params?.videoStoragePeriodicQuota)
                  : getVideoCount(entitlements?.params?.videoStoragePeriodicQuota)}
              </Header>
            </BlueHighlight>
          ) : (
            <Text variant={{ base: 'body-md', md: 'body-lg', xl: 'body-md' }}>
              {isStorageQuota
                ? getStorageQuota(entitlements?.params?.videoStoragePeriodicQuota)
                : getVideoCount(entitlements?.params?.videoStoragePeriodicQuota)}
            </Text>
          )}
        </StyledRow>
        {getBandwidth && planData.metadata.isBandwidthProduct && (
          <StyledRow>
            <div>
              <Speedometer
                h={{ base: rem(16), md: rem(20), xl: rem(16) }}
                w={{ base: rem(16), md: rem(20), xl: rem(16) }}
              />
            </div>
            <Text variant={{ base: 'body-md', md: 'body-lg', xl: 'body-md' }}>
              {getBandwidth(entitlements?.params?.bandwidth?.periodicQuota)}
            </Text>
          </StyledRow>
        )}
      </StorageQuotaContainer>
    </PlanQuotaContainer>
  );
};

export default PlanCardQuota;
